import {createSelector} from '@reduxjs/toolkit';
import {
  AvailabilityApiResponseDay,
  ProposedService,
} from '../../../server/routes/availability/types';
import {RootState} from '../../hooks/store';
import {generateAddress} from '../../utils/generateAddress';
import {isAvailabiltyRoute} from '../../utils/isRoute';
import {formatPrice} from '../../../shared/utils/formatPrice';
import {AvailabilityState} from './availability/slice';
import {selectChannelData} from './channel/slice';
import {ANY_EMPLOYEE} from './constants';
import {RescheduledOrderState} from './reschedule/slice';

export const selectRootState = (
  state: RootState,
): AvailabilityState | (AvailabilityState & RescheduledOrderState) => {
  if (isAvailabiltyRoute()) {
    return state.availability;
  }
  return state.reschedule;
};

export const selectAvailabilityRequestStatus = (state: RootState) =>
  selectRootState(state).availabilityStatusRequest;

export const selectAppRequestStatus = (state: RootState) =>
  selectRootState(state).appStatusRequest;

export const selectEmployeesState = (state: RootState) =>
  selectRootState(state).data.employees;

export const selectEmployees = createSelector(
  [selectEmployeesState],
  (employees) => {
    return Object.values(employees).map((employee) => {
      return {
        employeeName: employee.name,
        avatarUrl: employee.image?.uris['68x68'],
        id: employee.id,
      };
    });
  },
);

export const selectServices = (state: RootState) =>
  selectRootState(state).data.services;

export const selectSelected = (state: RootState) =>
  selectRootState(state).selected;

export const selectProposedServices = createSelector(
  [selectServices, selectSelected],
  (services, selected) => {
    const proposedServices: ProposedService[] = services.map((service) => {
      return {
        menuItemId: service.id,
        optionIds: service.skuIds,
        employeeId: selected.employeeMultiSelect[service.id],
      };
    });
    return proposedServices;
  },
);

export const selectSkus = (state: RootState) =>
  selectRootState(state).data.skus;

export const getDays = (state: RootState) => selectRootState(state).data.days;

export const selectSelectedEmployee = (state: RootState) =>
  selectRootState(state).selected.employee;

export const selectSelectedMultiEmployee = (state: RootState) =>
  selectRootState(state).selected.employeeMultiSelect;

export const selectIsMultiStaffSelectionEnabled = (state: RootState) => {
  return selectRootState(state).data.multiStaffSelectionExperimentEnabled;
};

export const selectIsEmployeeSelectionAllowed = (state: RootState) =>
  selectRootState(state).data.employeeSelectionAllowed;

export const selectShowMultiStaffSelection = createSelector(
  [selectIsMultiStaffSelectionEnabled, selectIsEmployeeSelectionAllowed],
  (isMultiStaffSelectionEnabled, isEmployeeSelectionAllowed) =>
    isMultiStaffSelectionEnabled && isEmployeeSelectionAllowed,
);

export const selectDays = createSelector(
  [
    getDays,
    selectSelectedEmployee,
    selectSelectedMultiEmployee,
    selectShowMultiStaffSelection,
    selectProposedServices,
  ],
  (
    days,
    selectedEmployee,
    selectedMultiEmployee,
    shouldShowMultiStaffSelection,
  ) => {
    const multiStaffNoEmployeeSelected =
      shouldShowMultiStaffSelection &&
      Object.keys(selectedMultiEmployee).length === 0;

    const singleStaffNoEmployeeSelected =
      !shouldShowMultiStaffSelection && selectedEmployee === ANY_EMPLOYEE;

    const noEmployeeSelected =
      multiStaffNoEmployeeSelected || singleStaffNoEmployeeSelected;

    if (noEmployeeSelected) {
      return days;
    }

    return filterDaysBasedOnEmployeeAvailability({
      days,
      selectedEmployee,
      selectedMultiEmployee,
      shouldShowMultiStaffSelection,
    });
  },
);

export const selectInitialDate = (state: RootState) =>
  selectRootState(state).selected.initialDate;

export const selectLastDate = (state: RootState) => {
  const days = Object.values(selectDays(state));
  return days[days.length - 1].date;
};

export const selectPaginationFutureRequestStatus = (state: RootState) =>
  selectRootState(state).paginationFutureStatusRequest;

export const selectSelectedDate = (state: RootState) =>
  selectRootState(state).selected.date;

export const selectTimeslots = createSelector(
  [selectDays, selectSelectedDate],
  (days, selectedDate) => {
    if (selectedDate) {
      return days[selectedDate]?.times || [];
    } else return [];
  },
);

export const selectTimeslotsInitialDate = createSelector(
  [selectDays, selectInitialDate],
  (days, initialDate) => {
    if (initialDate) {
      return days[initialDate]?.times || [];
    } else {
      return [];
    }
  },
);

export const selectHasInitialAvailability = createSelector(
  [getDays],
  (days) => {
    return Object.values(days).some((day) => day.isAvailable);
  },
);

export const selectIsMoreDaysInFuture = (state: RootState) =>
  selectRootState(state).data.moreDaysInTheFuture;

export const selectSelectedStartTime = (state: RootState) =>
  selectRootState(state).selected.startTime;

export const selectSelectedHasDiscount = (state: RootState) =>
  selectRootState(state).selected.hasDiscount;

export const selectSelectedTotalPrice = (state: RootState) =>
  selectRootState(state).selected.totalPrice;

export const selectSelectedOriginalPrice = (state: RootState) =>
  selectRootState(state).selected.originalPrice;

export const isRequestPending = (state: RootState) => {
  const status = selectAvailabilityRequestStatus(state);
  return status !== 'pending';
};

export const selectVenueDetails = (state: RootState) => {
  if (isAvailabiltyRoute()) {
    return {
      name: state.venue.data.name,
      address: generateAddress({
        addressLines: state.venue.data.address.addressLines,
        postalCode: state.venue.data.address.postalCode,
      }),
    };
  }
  return {
    name: state.reschedule.venue.name,
    address: generateAddress({
      addressLines: state.reschedule.venue.address.addressLines,
      postalCode: state.reschedule.venue.address.postalCode,
    }),
  };
};

const selectRescheduleDeposit = (state: RootState) =>
  state.reschedule.venue.deposit;

export const selectRescheduleDepositFormatted = createSelector(
  [selectRescheduleDeposit, selectChannelData],
  (deposit, channel) => {
    if (!deposit) {
      return undefined;
    }

    return {
      amount: formatPrice(deposit.amount, channel),
      remainderAmount: formatPrice(deposit.remainderAmount, channel),
    };
  },
);

const filterDaysBasedOnEmployeeAvailability = ({
  days,
  selectedEmployee,
  selectedMultiEmployee,
  shouldShowMultiStaffSelection,
}: {
  days: Record<string, AvailabilityApiResponseDay>;
  selectedEmployee: number | 'any-employee';
  selectedMultiEmployee: {[key: string]: number | undefined};
  shouldShowMultiStaffSelection?: boolean;
}) => {
  const filteredDays: Record<string, AvailabilityApiResponseDay> = {};
  const multiEmployees = Object.values(selectedMultiEmployee);

  Object.entries(days).forEach(([date, {dayNumber, dayName, times}]) => {
    const day: AvailabilityApiResponseDay = {
      date,
      dayNumber,
      dayName,
      isAvailable: true,
      times: [],
    };

    let timeslots = [];

    if (shouldShowMultiStaffSelection) {
      timeslots = times.filter((timeslot) => {
        return multiEmployees.every((id) => {
          if (id) {
            return timeslot.employeeIds.includes(id);
          }
        });
      });
    } else {
      if (selectedEmployee === 'any-employee') {
        return times;
      }
      timeslots = times.filter((timeslot) => {
        return timeslot.employeeIds.includes(selectedEmployee);
      });
    }
    day.times = timeslots;
    day.isAvailable = Boolean(timeslots.length);
    filteredDays[day.date] = day;
  });
  return filteredDays;
};

export const selectNextAvailableDate = createSelector(
  [selectDays, selectSelectedDate],
  (days, selectedDate) => {
    const daysToArray = Object.values(days);
    const currentDateIndex = daysToArray.findIndex(
      (day) => day.date === selectedDate,
    );
    if (currentDateIndex === -1) {
      return undefined;
    }
    return daysToArray.splice(currentDateIndex).find((day) => day.isAvailable);
  },
);

export const selectNextAvailableDateFromInitialDate = createSelector(
  [selectDays, selectInitialDate],
  (days, initialDate) => {
    const daysToArray = Object.values(days);
    const currentDateIndex = daysToArray.findIndex(
      (day) => day.date === initialDate,
    );
    if (currentDateIndex === -1) {
      return undefined;
    }
    return daysToArray.splice(currentDateIndex).find((day) => day.isAvailable);
  },
);

export const selectDaysUntilEnd = createSelector(
  [selectDays, selectSelectedDate],
  (days, selectedDate) => {
    const daysToArray = Object.values(days);
    const currentDateIndex = daysToArray.findIndex(
      (day) => day.date === selectedDate,
    );
    if (currentDateIndex === -1) {
      return undefined;
    }
    return daysToArray.splice(currentDateIndex).length;
  },
);

export const selectSkusDuration = createSelector([selectSkus], (skus) => {
  let duration = 0;
  Object.values(skus).forEach((sku) => {
    duration += sku.durationMinutes;
  });
  return duration;
});

export const selectAvailableDaysFirstWeek = createSelector(
  [selectDays],
  (days) => {
    const daysToArray = Object.values(days);
    const currentWeek = daysToArray.slice(0, 7);
    return currentWeek.filter((day) => day.isAvailable).length;
  },
);
